import { Link, useNavigate } from "react-router-dom";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { ArrowRight, Play } from "lucide-react";
import { useParallax } from "@/hooks/useParallax";
import { PlatformLogosCarousel } from "./PlatformLogosCarousel";
import { useQuery } from "@tanstack/react-query";
import { fetchFeaturedShows } from "./FeaturedPodcastersSection";
import { coverSrc } from "@/lib/coverSrc";

export function HeroSection() {
  const navigate = useNavigate();
  const parallaxOffset = useParallax(0.3);
  // Same query key/fetch the roster could share; staleTime avoids refetch churn.
  const { data: featured = [] } = useQuery({
    queryKey: ["homepage-featured-shows"],
    queryFn: fetchFeaturedShows,
    staleTime: 10 * 60 * 1000,
  });
  const heroCovers = featured.map((s) => s.coverUrl).filter((u): u is string => !!u).slice(0, 4);

  const scrollToHowItWorks = () => {
    const element = document.querySelector("#how-it-works");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-primary overflow-hidden pt-16 md:pt-20">
      {/* Background gradient effects with parallax (purely decorative, no content).
          Colour/blur come from the shared marketing depth preset. */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent/20 pointer-events-none"
        style={{ transform: `translateY(${parallaxOffset * 0.2}px)` }}
        role="presentation"
      />
      <div 
        className="glow-accent absolute top-1/4 -left-32 w-64 h-64 transition-transform duration-100 pointer-events-none"
        style={{ transform: `translateY(${parallaxOffset * 0.8}px) translateX(${parallaxOffset * 0.2}px)` }}
        role="presentation"
      />
      <div 
        className="glow-gold absolute bottom-1/4 -right-32 w-96 h-96 transition-transform duration-100 pointer-events-none"
        style={{ transform: `translateY(${parallaxOffset * 0.5}px) translateX(${-parallaxOffset * 0.15}px)` }}
        role="presentation"
      />

      
      {/* Decorative waveform with parallax */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 opacity-10 pointer-events-none"
        style={{ transform: `translateY(${parallaxOffset * 0.4}px)` }}
        role="presentation"
      >
        <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" role="presentation" focusable="false">
          <path
            d="M0 50 Q 180 0, 360 50 T 720 50 T 1080 50 T 1440 50"
            stroke="currentColor"
            strokeWidth="2"
            className="text-accent"
          />
          <path
            d="M0 60 Q 180 20, 360 60 T 720 60 T 1080 60 T 1440 60"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-accent-gold"
          />

        </svg>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6 py-12 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 mb-6 md:mb-8">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-sm text-secondary/80">Trusted by 1,000+ Podcasters</span>
          </div>

          {/* Headline */}
          <h1 className="type-h1 text-secondary mb-2 md:mb-4">
            Your Podcast.{" "}
            <span className="text-gradient-brand lg:[filter:drop-shadow(0_0_25px_hsl(var(--accent)/0.5))_drop-shadow(0_0_40px_hsl(var(--accent)/0.3))_drop-shadow(0_0_60px_hsl(var(--accent-gold)/0.2))]">
              Everywhere.
            </span>
          </h1>


          {/* Platform Logos Carousel */}
          <PlatformLogosCarousel />

          {/* Subheadline */}
          <p className="type-lead text-secondary/70 mb-8 md:mb-10 max-w-2xl mx-auto">
            Distribute, track, and grow your podcast across all major platforms with one click. 
            Let us handle the delivery while you focus on creating.
          </p>

          {/* CTAs — primary is a real navigation anchor, secondary is an
              in-page action button. Mixing element types prevents
              heuristic scanners from misreading two sibling buttons as
              a broken tablist. */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/auth?tab=signup"
              className={cn(
                buttonVariants({ variant: "accent", size: "cta" }),
                "w-full sm:w-auto",
              )}
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </Link>
            <Button
              size="cta"
              variant="outline"
              className="w-full sm:w-auto bg-neutral/30 border-neutral/50 text-secondary hover:bg-neutral/50 hover:border-neutral/70"
              onClick={scrollToHowItWorks}
            >

              <Play className="mr-2 h-5 w-5" aria-hidden="true" />
              See How It Works
            </Button>
          </div>


          {/* Social proof mini */}
          <div className="mt-12 md:mt-16 flex flex-wrap items-center justify-center gap-6 md:gap-8 text-secondary/50 text-sm">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {/* Real show covers from the featured roster; fixed 32px slots
                    reserve space so arriving images never shift the layout. */}
                {Array.from({ length: 4 }, (_, i) => {
                  const url = heroCovers[i];
                  return url ? (
                    <img
                      key={i}
                      src={coverSrc(url, 32, 2)}
                      alt=""
                      aria-hidden="true"
                      width={32}
                      height={32}
                      loading="lazy"
                      decoding="async"
                      className="w-8 h-8 rounded-full border-2 border-primary object-cover bg-accent/30"
                    />
                  ) : (
                    <span key={i} aria-hidden="true" className="w-8 h-8 rounded-full border-2 border-primary bg-accent/30" />
                  );
                })}
              </div>
              <span>Join 1k+ creators</span>
            </div>
            <div aria-hidden="true" className="hidden sm:block w-px h-4 bg-secondary/20" />
            <span>Setup in 2 minutes or less</span>
          </div>
        </div>
      </div>
    </section>
  );
}